import { useState, useEffect, useCallback } from "react";
import "./MediaViewer.css";

export default function MediaViewer({ media, initialIndex = 0, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const currentMedia = media[currentIndex];
  const isVideo = currentMedia?.type === "video" || currentMedia?.url?.includes("video");

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [currentIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < media.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [currentIndex, media.length]);

  const handleKeyDown = useCallback(
    (e) => {
      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowLeft":
          handlePrev();
          break;
        case "ArrowRight":
          handleNext();
          break;
        default:
          break;
      }
    },
    [onClose, handlePrev, handleNext]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [handleKeyDown]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    setZoom((prev) => {
      const newZoom = Math.max(prev - 0.5, 1);
      if (newZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  const handleWheel = (e) => {
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDownload = async () => {
    try {
      let downloadUrl = currentMedia.url;

      // For Cloudinary URLs, add fl_attachment to force download
      if (downloadUrl.includes('cloudinary.com')) {
        if (downloadUrl.includes('/image/upload/')) {
          downloadUrl = downloadUrl.replace('/image/upload/', '/image/upload/fl_attachment/');
        } else if (downloadUrl.includes('/video/upload/')) {
          downloadUrl = downloadUrl.replace('/video/upload/', '/video/upload/fl_attachment/');
        }
      }

      // Fetch the file as blob for proper download
      const response = await fetch(downloadUrl);
      const blob = await response.blob();

      // Create object URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;

      // Generate filename
      const extension = isVideo ? "mp4" : "jpg";
      const filename = currentMedia.name || `download_${Date.now()}.${extension}`;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        document.body.removeChild(link);
      }

      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download error:", error);
      // Fallback: open in new tab
      window.open(currentMedia.url, "_blank");
    }
  };

  return (
    <div className="media_viewer_overlay" onClick={onClose}>
      <div className="media_viewer_container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="media_viewer_header">
          <div className="media_info">
            <span className="media_counter">
              {currentIndex + 1} / {media.length}
            </span>
          </div>
          <div className="media_actions">
            <button className="action_btn" onClick={handleZoomOut} title="Zoom Out">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M19 13H5v-2h14v2z" />
              </svg>
            </button>
            <button className="action_btn" onClick={handleZoomIn} title="Zoom In">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
            </button>
            <button className="action_btn" onClick={handleDownload} title="Download">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
              </svg>
            </button>
            <button className="action_btn close_btn" onClick={onClose} title="Close">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path
                  fill="currentColor"
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Media Content */}
        <div
          className="media_content"
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {media.length > 1 && currentIndex > 0 && (
            <button className="nav_btn prev" onClick={handlePrev}>
              <svg viewBox="0 0 24 24" width="32" height="32">
                <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </button>
          )}

          {isVideo ? (
            <video
              src={currentMedia.url}
              controls
              autoPlay
              className="media_video"
              style={{
                transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
              }}
            />
          ) : (
            <img
              src={currentMedia.url}
              alt="Media"
              className="media_image"
              draggable={false}
              style={{
                transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
              }}
            />
          )}

          {media.length > 1 && currentIndex < media.length - 1 && (
            <button className="nav_btn next" onClick={handleNext}>
              <svg viewBox="0 0 24 24" width="32" height="32">
                <path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </button>
          )}
        </div>

        {/* Thumbnails */}
        {media.length > 1 && (
          <div className="media_thumbnails">
            {media.map((item, index) => (
              <button
                key={index}
                className={`thumbnail ${index === currentIndex ? "active" : ""}`}
                onClick={() => {
                  setCurrentIndex(index);
                  setZoom(1);
                  setPosition({ x: 0, y: 0 });
                }}
              >
                <img src={item.thumbnail || item.url} alt="" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
